import { Fragment, type ReactNode } from 'react';
import { PLATZHALTER, type FirmaMails, type MailAdresse } from '../../../shared/kampagnen';
import { aktion, fmt } from '../../daten';
import { Symbol, vars } from '../../komponenten/Grundbausteine';
import { gehe } from '../../ort';

// Gemeinsame Bausteine des Reiters Kampagnen (Prototyp: zTone, pct, setRow, startCheck, subSeg, tplHtml)

export const zTon = (z: string) => z === 'läuft' ? 'ok' : z === 'pausiert' ? 'warn' : z === 'Entwurf' ? 'accent' : 'neutral';
export const pct = (a: number, b: number) => b ? (a / b * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' %' : '–';
export const je100 = (a: number, b: number) => b ? (a / b * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 }) : '–';
export const zuKampagne = (id: number, sub = '') => gehe(`/akquise/kampagnen/k/${id}${sub ? '/' + sub : ''}`);

/** Zeile der Einstellungen: Titel, Erklärung, rechts das Bedienelement. */
export function SetRow({ titel, text, children }: { titel: ReactNode; text?: ReactNode; children: ReactNode }) {
  return <div className="row-set"><div><span style={{ fontWeight: 500 }}>{titel}</span>{text ? <span className="meta">{text}</span> : null}</div><div className="ctl">{children}</div></div>;
}

/** Segment der Unteransicht einer Kampagne (in .seg-scroll, wie im Prototyp). */
export function SubSeg({ wahl, wert, setzen }: { wahl: [string, string][]; wert: string; setzen: (w: string) => void }) {
  const i = Math.max(0, wahl.findIndex(w => w[0] === wert));
  return (
    <div className="seg-scroll"><div className="seg" id="kSubSeg" style={vars({ '--n': wahl.length, '--i': i })} role="group" aria-label="Ansicht">
      <span className="seg-thumb" aria-hidden="true" />
      {wahl.map(([k, l], j) => <button key={k} data-ksub={k} aria-pressed={j === i} onClick={() => j !== i && setzen(k)}>{l}</button>)}
    </div></div>
  );
}

export interface Terminlink { closer: string; titel: string; angebot: string; link: string }

/** Terminlink der Kampagne: automatisch der des Closers für das Angebot (CRM › Team), überschreibbar mit einem anderen hinterlegten Link. */
export function TerminlinkWahl({ closer, angebot, wert, links, setzen, aus }: { closer: string; angebot: string; wert: string; links: Terminlink[]; setzen: (link: string) => void; aus?: boolean }) {
  const eigen = links.find(l => l.closer === closer && l.angebot === angebot)?.link ?? '';
  const andere = links.filter(l => l.link !== eigen);
  return (<>
    <select className="input plain" data-ktl disabled={aus} value={wert} onChange={e => setzen(e.target.value)}>
      <option value="">{eigen ? `automatisch: ${eigen}` : `kein Link für ${closer || 'den Closer'} und ${angebot}`}</option>
      {andere.map(l => <option key={`${l.closer}|${l.titel}|${l.link}`} value={l.link}>{l.closer} · {l.titel}: {l.link}</option>)}
      {wert && !links.some(l => l.link === wert) && <option value={wert}>{wert}</option>}
    </select>
    {!eigen && !wert && <span className="meta" style={{ color: 'var(--crit)' }}>Ohne Terminlink startet die Kampagne nicht: in CRM › Team bei {closer || 'der Person'} unter „Buchungsseiten“ eine Seite für {angebot} eintragen.</span>}
  </>);
}

export interface StartPunkt { ok: boolean; text: string; hilfe: string }

/** Startprüfung einer Kampagne im Entwurf. */
export function StartCheck({ punkte, starten, uebernehmen }: { punkte: StartPunkt[]; starten: () => void; uebernehmen?: { name: string; los: () => void } | null }) {
  const ok = punkte.every(p => p.ok);
  return (
    <div className={ok ? 'callout' : 'callout warn'}><Symbol id={ok ? 'check' : 'alert'} /><div>
      <strong>{ok ? 'Bereit zum Start' : 'Noch nicht startklar'}</strong>
      <ul className="check">{punkte.map(p => <li key={p.text} className={p.ok ? 'ok' : 'no'}><span className="dot"><Symbol id={p.ok ? 'check' : 'x'} /></span><span>{p.text}{!p.ok && p.hilfe ? <span className="meta"> · {p.hilfe}</span> : null}</span></li>)}</ul>
      <div className="assign"><button className="btn btn-primary btn-sm" data-kstate="läuft" disabled={!ok} onClick={starten}>Kampagne starten</button>{uebernehmen && <button className="btn btn-sm" onClick={uebernehmen.los}>Vorlagen aus „{uebernehmen.name}“ übernehmen</button>}</div>
    </div></div>
  );
}

/** Text einer Vorlage mit hervorgehobenen Platzhaltern. */
export function MitPlatzhaltern({ text }: { text: string }) {
  const teile = text.split(/(\{[^}]+\})/g);
  return <>{teile.map((t, i) => /^\{[^}]+\}$/.test(t) ? <span key={i} className="ph">{t}</span> : t)}</>;
}

export const PLATZHALTER_LISTE = PLATZHALTER;

/** Kopf und Text einer Mail (Prototyp .mail). */
export function Mail({ kopf, children }: { kopf: [string, ReactNode][]; children: ReactNode }) {
  return <div className="mail"><dl className="mail-head">{kopf.map(([d, v]) => <Fragment key={d}><dt>{d}</dt><dd>{v}</dd></Fragment>)}</dl><div className="mail-body">{children}</div></div>;
}

/** Führt eine Aktion aus und meldet das Ergebnis als Hinweis; gibt true zurück, wenn sie geklappt hat. */
export async function tun(hinweis: (t: string) => void, name: string, daten: unknown, nachher?: () => void): Promise<boolean> {
  try {
    const r = await aktion<{ text?: string }>(name, daten);
    if (r.text) hinweis(r.text);
    nachher?.();
    return true;
  } catch (e) {
    hinweis(`Geht nicht: ${(e as Error).message}`);
    return false;
  }
}

export const zahl = (n: number | null | undefined) => n === null || n === undefined ? '–' : fmt(n);

export interface MailEintrag { am: string; art: string; betreff: string; text: string; bounce: boolean }
/** Was der Firma schon geschrieben wurde und was zurückkam (Telefonkampagne, Opener) */
/** Wer hinter der Nummer steht, gut sichtbar (Prototyp callWer). */
export function CallWer({ weg, ap, pos }: { weg: string; ap: string; pos: string }) {
  const persoenlich = weg === 'Telefon persönlich';
  return <p className="call-wer"><span className={`state ${persoenlich ? 'ok' : 'neutral'}`}>{persoenlich ? 'Persönliche Nummer' : 'Allgemeine Nummer'}</span>{ap ? <span><strong>{ap}</strong>{pos ? `, ${pos}` : ''}</span> : <span>Ansprechpartner unbekannt: nach der Geschäftsführung fragen</span>}</p>;
}

const MailAdr = ({ m }: { m: MailAdresse }) => m.tot ? <div>{m.adresse}<span className="meta"> · {m.tot}</span></div> : <div><a href={`mailto:${m.adresse}`}>{m.adresse}</a></div>;
/** Website und Mails aus dem Datensatz der Firma, als Zeilen einer dl.facts (Prototyp crmMask). */
export function FirmaKontakt({ web, z }: { web: string; z: FirmaMails }) {
  return <>
    <dt>Website</dt><dd>{web ? <a href={`https://${web}`} target="_blank" rel="noopener">{web}</a> : <span className="meta">keine eigene Website</span>}</dd>
    {z.mailP.length > 0 && <><dt>Mail persönlich</dt><dd>{z.mailP.map(m => <MailAdr key={m.adresse} m={m} />)}</dd></>}
    {z.mailA.length > 0 && <><dt>Mail allgemein</dt><dd>{z.mailA.map(m => <MailAdr key={m.adresse} m={m} />)}</dd></>}
    {!z.mailP.length && !z.mailA.length && <><dt>Mail</dt><dd><span className="meta">noch keine; im Gespräch erfragen</span></dd></>}
  </>;
}
/** Hinweis unter dem Feld „An“: die vorbelegte Adresse ist nur ein Vorschlag (Prototyp crmForm). */
export const MailPruefen = ({ beste }: { beste: string }) => <><span className="state warn">{beste ? 'Hinterlegte Mail, bitte im Gespräch prüfen' : 'Keine Mail hinterlegt, bitte erfragen'}</span><span className="meta">Eine neue Adresse wird beim Kunden gespeichert.</span></>;

export function MailVerlauf({ mails }: { mails?: MailEintrag[] }) {
  if (!mails?.length) return null;
  return <div className="fld"><span className="lbl">Mails</span><ul className="log">{mails.map((m, i) => <li key={i}><time>{m.am}</time><details><summary>{m.art}: {m.betreff}{m.bounce && <span className="state warn"> unzustellbar</span>}</summary><p className="meta" style={{ whiteSpace: 'pre-wrap' }}>{m.text}</p></details></li>)}</ul></div>;
}
